import {
  ActionBar,
  Button,
  Checkbox,
  Dropdown,
  DropdownMenu,
  EmptyState,
  ImportIcon,
  LazyLoader,
  LessonIllustration,
  showToast,
} from '@bp/ui-components';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { getFragmentData, graphql } from 'client/gql';
import { BpCard } from 'components/BpCard/BpCard';
import { Form, Formik } from 'formik';
import { SettingsImportPlanungSubpageOutletContext } from 'pages/Settings/subpages/SettingsImportPlanungSubpage';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { backendApi } from 'utils/backendApi';
import { createForeignRefId, SyncMaster } from 'utils/create-foreignRefId';
import { naturalStringSort } from 'utils/stringHelper';
import { showUserDbErrorToast } from 'utils/toasts';
import { JobStatus, SortDirection } from '../../../client/gql/graphql';
import { OrganizationConfigContext } from '../../../context/OrganizationConfigContext';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { ForeignSyncMetasFieldsDocument } from '../queries/queries';
import { NotImportableLessonsTable } from '../tables/NotImportableLessonsTable';
import { PlanungImportLessonsJobTable } from '../tables/PlanungImportLessonsJobTable';
import { PlanungLesson } from '../types';
import styles from './_PlanungImportForms.module.scss';
import { PlanungImportLessonsFormRow } from './PlanungImportLessonsFormRow';

export const PlanungImportLessonsFormFragment = graphql(`
  fragment PlanungImportLessonsFormFields on Query {

    groups(
      options: { sort: [{ name: ASC }] }
      where: { schoolYear: { uuid: $schoolYearUuid }, organization: { uuid: $organizationUuid } }
    ) {
      __typename
      uuid
      name
    }
    foreignSyncMetas(where: { syncSource: "planung" }) {
      __typename
      foreignRefId
      globalSubject
      ignore
      syncTarget {
        ... on Subject {
          uuid
          __typename
        }
        ... on Class {
          uuid
          __typename
        }
        ... on ClassGroup {
          uuid
          __typename
        }
        ... on Group {
          uuid
          __typename
        }
        ... on Profile {
          uuid
          __typename
        }
        ... on Division {
          uuid
          __typename
        }
      }
    }
  }
`);

const PlanungImportLessonsFormDocument = graphql(`
  query PlanungImportLessonsFormQuery($lessonJobWhere: LessonJobWhere, $lessonJobOptions: LessonJobOptions, $schoolYearUuid: ID!) {
    ...LessonJobsTableFields
  }
`);

type PlanungImportLessonsFormType = {
  lessons: LessonModel[];
};

type LessonModel = {
  uuid: string;
  selected: boolean;
  name: string;
  connectCourseUuid: string | null;
  connected: boolean;
  missingClasses: boolean;
  missingSubjects: boolean;
  missingTeachers: boolean;
};

const PlanungCancelLessonJobDocument = graphql(`
  mutation PlanungCancelSynchronisationLesson($uuid: ID!, $update: LessonJobUpdateInput) {
    updateLessonJobs(where: { uuid: $uuid }, update: $update) {
      lessonJobs {
        uuid
      }
    }
  }
`);

export const PlanungImportLessonsForm = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const { currentSchoolYear } = useContext(OrganizationConfigContext);
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const perms = usePermissionChecker();
  const [, cancelLessonJob] = useMutation(PlanungCancelLessonJobDocument);
  const { bpData, planungSchoolyear, bpSchoolyear, refetch } =
    useOutletContext<SettingsImportPlanungSubpageOutletContext>();

  const { data: planungData } = useReactQuery<PlanungLesson[]>({
    queryKey: ['planungLessons', planungSchoolyear],
    queryFn: async () => {
      const response = await backendApi.fetch(`/planung/lessons/${planungSchoolyear}`);
      return await response.json();
    },
    enabled: !!planungSchoolyear,
  });
  const handleCancelJob = async (uuid: string) => {
    await cancelLessonJob({ uuid, update: { status: JobStatus.Cancelled } });
    refetch({ requestPolicy: 'network-only' });
  };

  const metaFragment = getFragmentData(ForeignSyncMetasFieldsDocument, bpData);
  const formFragment = getFragmentData(PlanungImportLessonsFormFragment, bpData);

  const foreignSyncMetas = metaFragment?.foreignSyncMetas ?? [];
  const courses = formFragment?.groups ?? [];

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState<string | null>(null);
  const [lessonModel, setLessonModel] = useState<LessonModel[]>([]);

  const [{ data }, fetch] = useQuery({
    query: PlanungImportLessonsFormDocument,
    variables: {
      schoolYearUuid: bpSchoolyear,
      lessonJobWhere: {
        organizationUuid,
      },
      lessonJobOptions: {
        sort: [{ createdAt: SortDirection.Desc }],
      },
    },
    pause: !currentSchoolYear,
  });

  const handleImport = async () => {
    if (currentSchoolYear) {
      setLoading(true);
      const intervalId = setInterval(() => {
        fetch({ requestPolicy: 'network-only' });
      }, 10_000);

      setTimeout(() => {
        clearInterval(intervalId);
      }, 1200000);

      const resp = await backendApi.post(
        `/planung/import/lessons`,
        JSON.stringify({
          planungSchoolYear: planungSchoolyear,
          organizationUuid,
        }),
      );

      if (resp.status === 200) {
        showToast(t('planungImport.importStarted'), {
          autoClose: 1200000,
          type: 'success',
          showCloseButton: false,
          onClose: () => {
            fetch();
          },
        });
      } else {
        showUserDbErrorToast();
      }
      setLoading(false);
    }
  };

  const initialValues: PlanungImportLessonsFormType = {
    lessons: [],
  };

  function handleSelect(uuid: string, select: boolean) {
    const newModel = lessonModel.map((pl) => ({
      ...pl,
      selected: pl.uuid === uuid ? select : pl.selected,
      connectCourseUuid: pl.uuid === uuid && !pl.connectCourseUuid && !select ? null : pl.connectCourseUuid,
    }));
    setLessonModel(newModel);
  }

  function handleLessonSelect(uuid: string, connectCourseUuid: string | null) {
    const newModel = lessonModel.map((pl) => ({
      ...pl,
      connectCourseUuid: pl.uuid === uuid ? connectCourseUuid : pl.connectCourseUuid,
    }));
    setLessonModel(newModel);
  }

  function handleSelectAll(select: boolean) {
    const newModel = lessonModel.map((pl) => ({
      ...pl,
      selected: filteredModel.includes(pl) ? select : pl.selected,
      connectCourseUuid: !pl.connectCourseUuid && !select ? null : pl.connectCourseUuid,
    }));
    setLessonModel(newModel);
  }

  function handleSelectImported(select: boolean) {
    const newModel = lessonModel.map((pl) => ({
      ...pl,
      selected: filteredModel.includes(pl) && pl.connected ? select : pl.selected,
      connectCourseUuid: !pl.connectCourseUuid && !select ? null : pl.connectCourseUuid,
    }));
    setLessonModel(newModel);
  }

  function handleSelectNotImported(select: boolean) {
    const newModel = lessonModel.map((pl) => ({
      ...pl,
      selected: filteredModel.includes(pl) && !pl.connected ? select : pl.selected,
      connectCourseUuid: !pl.connectCourseUuid && !select ? null : pl.connectCourseUuid,
    }));
    setLessonModel(newModel);
  }

  const filteredModel = lessonModel
    .filter((pl) => (!search ? true : pl.name.toLowerCase().includes(search?.toLowerCase())))
    .sort((a, b) => {
      // imported
      if (a.connected !== b.connected) {
        return a.connected ? 1 : -1;
      }
      // sort by name
      return naturalStringSort(a.name, b.name);
    });

  const importableModel = filteredModel.filter(
    (pl) => !pl.missingTeachers && !pl.missingSubjects && !pl.missingTeachers,
  );

  const notImportableModel = filteredModel.filter(
    (pl) => pl.missingClasses || pl.missingSubjects || pl.missingTeachers,
  );

  const availableLessons: { uuid: string; name: string }[] = useMemo(() => {
    const connectedCourseUuid = lessonModel.map((pl) => pl.connectCourseUuid).filter((uuid): uuid is string => !!uuid);
    return courses
      .filter((c) => c && !connectedCourseUuid.includes(c.uuid))
      .map((c) => ({ uuid: c.uuid, name: c.name ?? '' }));
  }, [lessonModel, courses]);

  // Function to find matching courses by name
  const findMatchingCourse = useMemo(() => {
    return (lessonName: string): { uuid: string; name: string } | null => {
      const normalizedLessonName = lessonName.toLowerCase().trim();
      return availableLessons.find((course) => course.name.toLowerCase().trim() === normalizedLessonName) || null;
    };
  }, [availableLessons]);

  // Function to auto-fill matching courses
  const autoFillMatchingCourses = useCallback(() => {
    const newModel = lessonModel.map((pl) => {
      // Only auto-fill if no course is already selected and lesson is not connected
      if (!pl.connectCourseUuid && !pl.connected) {
        const matchingCourse = findMatchingCourse(pl.name);
        if (matchingCourse) {
          return {
            ...pl,
            connectCourseUuid: matchingCourse.uuid,
          };
        }
      }
      return pl;
    });
    setLessonModel(newModel);
  }, [lessonModel, findMatchingCourse]);

  useEffect(() => {
    const foreignRefIds = foreignSyncMetas.map((fsm) => fsm.foreignRefId);

    const filterClasses = (lesson: PlanungLesson) => {
      const planungClassUuids = lesson.lessonClasses.map((lc) => createForeignRefId(SyncMaster.planung, lc.class.uuid));
      return !planungClassUuids?.some((fsm) => foreignRefIds?.includes(fsm));
    };

    const filterSubjects = (lesson: PlanungLesson) => {
      const planungSubjectUuid = createForeignRefId(SyncMaster.planung, lesson.subject.uuid);
      return !foreignRefIds?.includes(planungSubjectUuid);
    };

    const filterTeachers = (lesson: PlanungLesson) => {
      const planungTeacherUuids = lesson.teachers.map((t) => createForeignRefId(SyncMaster.planung, t.uuid));
      return !planungTeacherUuids.some((pt) => foreignRefIds?.includes(pt));
    };

    const model: LessonModel[] =
      planungData
        ?.sort((a, b) => naturalStringSort(a.lessonName, b.lessonName))
        .map((pl) => {
          const fsm = foreignSyncMetas?.find(
            (fsm) => fsm.foreignRefId === createForeignRefId(SyncMaster.planung, pl.uuid),
          );
          const connected = !!fsm && !!fsm.syncTarget && fsm.syncTarget.__typename === 'Group';
          return {
            uuid: pl.uuid,
            selected: !connected,
            name: pl.lessonName ?? '',
            connectCourseUuid: connected ? (fsm.syncTarget?.uuid ?? null) : null,
            connected: connected,
            missingClasses: filterClasses(pl),
            missingSubjects: filterSubjects(pl),
            missingTeachers: filterTeachers(pl),
          };
        }) ?? [];
    setLessonModel(model);
  }, [planungData, foreignSyncMetas]);

  return (
    <>
      <BpCard
        noPadding
        header={{
          headline: t('planungImport.lessonsJobs'),
        }}
      >
        <PlanungImportLessonsJobTable query={data} handleCancelJob={handleCancelJob} handleImport={handleImport} />
      </BpCard>

      <Formik<PlanungImportLessonsFormType> initialValues={initialValues} onSubmit={handleImport}>
        <Form className={styles['planung-import-form']}>
          {loading ? (
            <LazyLoader size='xxl' borderRadius='m' forceHeight='25vh' />
          ) : (
            <BpCard className='mt-6' noPadding header={{ headline: t('planungImport.importLessons') }}>
              {perms.isOmniAdmin() && (
                <ActionBar
                  isOnWhite
                  onGlobalFilterChange={(value) => setSearch(value)}
                  className={styles['action-bar']}
                  extendedActionsLeft={
                    <div className={styles.overview}>
                      {`${t('planungImport.importOverview', {
                        sum: importableModel.filter((l) => l.selected).length,
                        total: filteredModel.length,
                      })}, ${t('planungImport.notImportable', { count: notImportableModel.length })}`}
                    </div>
                  }
                  extendedActionsRight={
                    <>
                      <Dropdown trigger={<Button hierarchy='tertiary'>{t('common.selection')}</Button>}>
                        <DropdownMenu
                          data={[
                            {
                              label: t('planungImport.autoFillMatching'),
                              onClick: () => autoFillMatchingCourses(),
                            },
                            {
                              label: t('planungImport.selectNotImported'),
                              onClick: () => handleSelectNotImported(true),
                            },
                            {
                              label: t('planungImport.deselectNotImported'),
                              onClick: () => handleSelectNotImported(false),
                            },
                            { type: 'ruler' },
                            {
                              label: t('planungImport.selectImported'),
                              onClick: () => handleSelectImported(true),
                            },
                            {
                              label: t('planungImport.deselectImported'),
                              onClick: () => handleSelectImported(false),
                            },
                            { type: 'ruler' },
                            { label: t('common.selectAll'), onClick: () => handleSelectAll(true) },
                            { label: t('planungImport.deselectAll'), onClick: () => handleSelectAll(false) },
                          ]}
                        />
                      </Dropdown>
                      <Button
                        type='submit'
                        icon={<ImportIcon />}
                        disabled={lessonModel.filter((l) => l.selected).length === 0}
                      >
                        {t('planungImport.createCourses')}
                      </Button>
                    </>
                  }
                />
              )}

              <div className={classNames(styles.form, styles.lessons)}>
                {importableModel.length === 0 && notImportableModel.length === 0 ? (
                  <EmptyState icon={<LessonIllustration />} title={t('lessons.noLessons')} forcedHeight='30vh' />
                ) : (
                  <>
                    {importableModel.length === 0 ? (
                      <EmptyState forcedHeight='15vh' />
                    ) : (
                      perms.isOmniAdmin() && (
                        <div className={styles.importable}>
                          <div className={styles.header}>
                            <div className={styles.checkbox}>
                              <Checkbox
                                onChange={(event) => {
                                  handleSelectAll(event.target.checked);
                                }}
                                checked={
                                  importableModel.length > 0 &&
                                  importableModel.filter((t) => t.selected).length === importableModel.length
                                }
                                name={`toggle-courses-all`}
                              />
                              <div>{t('planungImport.lessonInPlanung')}</div>
                            </div>
                            <div></div>
                            <div>{t('planungImport.courseInBp')}</div>
                            <div>{t('planungImport.connected')}</div>
                          </div>
                          {importableModel.map((pl) => (
                            <PlanungImportLessonsFormRow
                              key={pl.uuid}
                              uuid={pl.uuid}
                              selected={pl.selected}
                              name={pl.name}
                              lessons={availableLessons}
                              selectedLesson={
                                pl.connectCourseUuid && courses.find((c) => c.uuid === pl.connectCourseUuid)
                                  ? {
                                      value: courses.find((c) => c.uuid === pl.connectCourseUuid)?.uuid ?? '',
                                      label: courses.find((c) => c.uuid === pl.connectCourseUuid)?.name ?? '',
                                    }
                                  : null
                              }
                              connected={pl.connected}
                              onSelect={(isSelected) => handleSelect(pl.uuid, isSelected)}
                              onLessonSelect={(lessonUuid) => handleLessonSelect(pl.uuid, lessonUuid)}
                            />
                          ))}
                        </div>
                      )
                    )}
                    {
                      <div className={styles.missing}>
                        <div className={styles.reason}>{t('planungImport.notImportable')}</div>
                        <NotImportableLessonsTable
                          data={notImportableModel.map((pl) => {
                            const reasons: string[] = [];
                            if (pl.missingClasses) reasons.push(t('classes.title', { count: 1 }));
                            if (pl.missingSubjects) reasons.push(t('subjects.title', { count: 1 }));
                            if (pl.missingTeachers) reasons.push(t('teachers.titleSingular'));
                            return { name: pl.name, reason: reasons.join(', '), connected: false };
                          })}
                        />
                      </div>
                    }
                  </>
                )}
              </div>
            </BpCard>
          )}
        </Form>
      </Formik>
    </>
  );
};
